import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { editCommentAction, updateComment } from '../store/productCosting.actions';
import type { ProductCostingDispatch, TransactionMsg } from '../store/productCosting.types';
import { getInitials } from '../../../../Components/helper';
import { useProductCostingState } from '../ProductCostingContext';
import SubmitModal from '../Modal/SubmitModal';
import { EditIcon } from '../../../../bombiix-icon';

/** Comment row action glyph, sized to the compact action button. */
const ICON_SIZE_ACTION = 20;

/** One comment on the costing, as this panel renders it. */
interface CostingCommentEntry {
  _id?: string;
  message?: string;
  userImage?: string;
  userFullName?: string;
  isEdited?: boolean;
  isDefault?: boolean;
  createdBy?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

/** The redux slices this panel reads. */
interface CommentRootState {
  newProductCostingData: { costingCommentList?: CostingCommentEntry[]; editCommentData?: CostingCommentEntry | null };
  UserDetails: { userInfo?: { userId?: string } };
  BOMConfigDetails: { isProductArchived?: boolean };
}

/** The costing's comment thread, plus the inline "update comment" dialog. */
function ProductCostingComment(): React.JSX.Element {
  /* Typed dispatch, so the module's own thunks type-check at the call site. */
  const dispatch = useDispatch<ProductCostingDispatch>();
  const { devOrderId, productid } = useParams<{ devOrderId?: string; productid?: string }>();

  const { setInternalState, internalState, productCostingstate, CoastingAddEditRights } = useProductCostingState();

  const {
    productInfo: { supplierId, reference },
  } = productCostingstate;
  const { t } = useTranslation('common');

  const {
    newProductCostingData: { costingCommentList, editCommentData },
    UserDetails: { userInfo },
    BOMConfigDetails: { isProductArchived },
  } = useSelector((state: CommentRootState) => state);

  const [comState, setState] = useState<{ commentModal: boolean; message: string; loading: boolean }>({
    commentModal: false,
    message: '',
    loading: false,
  });

  const editMessage = async (commentData: CostingCommentEntry): Promise<void> => {
    await dispatch(editCommentAction(commentData));
    setState((state) => ({
      ...state,
      commentModal: true,
    }));
  };

  const updateMessage = async (): Promise<void> => {
    const obj = {
      message: comState.message,
      devOrderId,
      /* `supplierId` holds a populated supplier object on this state, not an id string — the same
         mismatch already documented in useDetailedPageHook. */
      costingId: productid as string,
      supplierId: (supplierId as unknown as { _id?: string })._id,
      _id: editCommentData?._id,
      isEdited: true,
      reference,
    };
    setState((state) => ({
      ...state,
      loading: true,
    }));
    const res = await dispatch(updateComment(obj, t as unknown as TransactionMsg));
    if (res) {
      await dispatch(editCommentAction(null as unknown as CostingCommentEntry));
      setState((state) => ({
        ...state,
        message: '',
        commentModal: false,
        loading: false,
      }));
    }
  };

  useEffect(() => {
    if (editCommentData) {
      setState((state) => ({
        ...state,
        message: editCommentData.message as string,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editCommentData]);

  const closeModal = async (): Promise<void> => {
    await dispatch(editCommentAction(null as unknown as CostingCommentEntry));
    setState((state) => ({
      ...state,
      commentModal: false,
      message: '',
    }));
  };
  return (
    <Container className="p-0">
      <div className="py-4">
        <div>
          <Row>
            <Col md={12}>
              <div className="form-group">
                <div>
                  <label>
                    <b>{t('DEVLOPMENT_ORDER.PRODUCT_COSTING_DETAIL.COMMENT')}</b>
                  </label>
                  <div>
                    <textarea
                      value={internalState?.costingComment}
                      className="form-control bordered-teatarea"
                      rows={4}
                      onChange={(e) =>
                        setInternalState((state) => ({
                          ...state,
                          costingComment: e.target.value,
                        }))
                      }
                      placeholder={t('DEVLOPMENT_ORDER.PRODUCT_COSTING_DETAIL.ENTER_TEXT')}
                      disabled={!CoastingAddEditRights}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <div>
                {Array.isArray(costingCommentList) &&
                  costingCommentList?.length > 0 &&
                  costingCommentList?.map((data) => {
                    return (
                      <div key={data?._id} className="mt-2">
                        <div className="comment-item">
                          <div className="comment-image">
                            {isEmpty(data.userImage) ? (
                              <span className="text-initial">{getInitials(data.userFullName ?? '')}</span>
                            ) : (
                              <img src={`${data.userImage}`} alt="images" width="80" height="80" />
                            )}
                          </div>
                          <div className="comment-detail">
                            <div className="user-time">
                              <span className="text-sentby">
                                {t('DEVLOPMENT_ORDER.PRODUCT_COSTING_DETAIL.SENT_BY')} {data.userFullName}
                              </span>
                              <span className="text-date">
                                {data.isEdited ? '(Edited) ' : ''}
                                {moment(data.updatedAt).format('LLLL')}
                              </span>
                            </div>
                            <div className="comment-text">
                              {data.message}
                              {userInfo?.userId === data.createdBy && !isProductArchived && !data?.isDefault && CoastingAddEditRights && (
                                <button
                                  type="button"
                                  className="btn-tbl-action"
                                  title={t('DEVLOPMENT_ORDER.PRODUCT_COSTING_DETAIL.EDIT')}
                                  onClick={() => editMessage(data)}
                                >
                                  <EditIcon size={ICON_SIZE_ACTION} />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </Col>
          </Row>
        </div>
      </div>

      {comState.commentModal && (
        <SubmitModal
          isOpen={comState.commentModal}
          title={t('DEVLOPMENT_ORDER.PRODUCT_COSTING_DETAIL.UPDATE_COMMENT')}
          onChange={(e) => {
            setState((state) => ({
              ...state,
              message: e.target.value,
            }));
            e.preventDefault();
          }}
          loading={comState.loading}
          onConfirm={() => updateMessage()}
          commentMsg={comState.message}
          handleModal={() => closeModal()}
        />
      )}
    </Container>
  );
}

export default ProductCostingComment;
